<template>
  <component
    :is="componentTag"
    :class="getClasses"
    :data-selector="dataSelector"
    data-testid="nds-button"
    :disabled="componentTag === 'button' && disabled"
    v-bind="componentTagProperties"
    @click="onClick"
  >
    <span
      class="ndsButton__slot"
      :class="{
        loading: loading || isBusy,
      }"
      data-testid="button-slot"
    >
      <NdsIcon
        v-if="iconLeft"
        class="ndsButton__icon"
        data-testid="button-icon-left"
        :icon="iconLeft"
        :size="iconSize[size]"
      />

      <slot />

      <NdsIcon
        v-if="iconRight"
        class="ndsButton__icon"
        data-testid="button-icon-right"
        :icon="iconRight"
        :size="iconSize[size]"
      />
    </span>

    <span
      v-if="loading || isBusy"
      class="loader"
    >
      <NdsSpinner
        :color="loaderColor"
        :size="NdsSpinnerSize.COMPACT"
      />
    </span>
  </component>
</template>

<script setup lang="ts">
import { ref, computed, type PropType } from 'vue'

import type { NdsIconType } from '@/components/NewDesignSystem/NdsIcon/NdsIcon.interfaces'
import { NdsSpinnerSize } from '@/components/NewDesignSystem/NdsSpinner/NdsSpinner.interfaces'
import {
  NdsButtonColor,
  NdsButtonSize,
  NdsButtonType,
  NdsButtonLoaderColorMapping,
  type NdsButtonLinkType,
  NdsButtonLinkTarget,
} from '@/components/NewDesignSystem/NdsButton/NdsButton.interfaces'
import { NdsColor } from '@/interfaces/NdsColors.interfaces'

import NdsSpinner from '@/components/NewDesignSystem/NdsSpinner/NdsSpinner.vue'
import NdsIcon from '@/components/NewDesignSystem/NdsIcon/NdsIcon.vue'

type ButtonTagType = keyof Pick<HTMLElementTagNameMap, 'a' | 'button'>
type ButtonTagPropertiesType = {
  type: NdsButtonType
}
type LinkTagPropertiesType = {
  href?: string
  target?: NdsButtonLinkTarget
  rel?: string
}

const props = defineProps({
  dataSelector: {
    type: String,
    required: false,
    default: 'button',
  },
  size: {
    type: String as PropType<NdsButtonSize>,
    default: NdsButtonSize.M,
  },
  color: {
    type: String as PropType<NdsButtonColor>,
    default: NdsButtonColor.PRIMARY,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  action: {
    type: [Function, null] as PropType<(() => unknown) | (() => Promise<unknown>) | null>,
    default: null,
  },
  iconLeft: {
    type: [String, null] as PropType<NdsIconType | null>,
    default: null,
  },
  iconRight: {
    type: [String, null] as PropType<NdsIconType | null>,
    default: null,
  },
  buttonType: {
    type: String as PropType<NdsButtonType>,
    default: NdsButtonType.BUTTON,
  },
  link: {
    type: Object as PropType<NdsButtonLinkType>,
  },
})

const emit = defineEmits<(e: 'click', event: Event) => void>()

const isBusy = ref<boolean>(false)

const iconSize: Record<NdsButtonSize, number> = {
  [NdsButtonSize.L]: 20,
  [NdsButtonSize.M]: 16,
  [NdsButtonSize.S]: 16,
  [NdsButtonSize.XS]: 16,
}

const componentTag = computed<ButtonTagType>(() => {
  return props.buttonType === NdsButtonType.LINK ? 'a' : 'button'
})

const buttonTagProperties = computed<ButtonTagPropertiesType>(() => ({
  type: props.buttonType,
}))

const linkTagProperties = computed<LinkTagPropertiesType>(() => ({
  href: props.link?.href,
  target: props.link?.target,
  rel: props.link?.rel,
}))

const componentTagProperties = computed<ButtonTagPropertiesType | LinkTagPropertiesType>(() => {
  return props.buttonType === NdsButtonType.LINK ? linkTagProperties.value : buttonTagProperties.value
})

const getClasses = computed(() => {
  return [`ndsButton -${props.size} -${props.color}`, { '-disabled': props.disabled }]
})

const loaderColor = computed<NdsColor>(() => {
  return NdsButtonLoaderColorMapping[props.color]
})

const onClick = async (event: Event) => {
  if (props.action !== null && !isBusy.value) {
    isBusy.value = true
    await props.action()
    isBusy.value = false
  }

  emit('click', event)
}
</script>

<style lang="postcss" scoped>
.ndsButton {
  display: inline-flex;
  font-weight: 500;
  cursor: pointer;
  text-align: center;
  font-style: normal;
  border: 1px solid transparent;
  border-radius: var(--spacing-1);
  gap: 8px;
  align-items: center;
  position: relative;

  &:focus {
    outline: none;
  }

  &__slot {
    display: flex;
    align-items: center;
    gap: 8px;
    text-wrap: nowrap;

    &.loading {
      opacity: 0;
    }
  }

  .loader {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: wait;
  }

  &.loading {
    position: absolute;
  }

  &.-xSmall {
    height: 24px;
    padding: 4px;
    font-size: 14px;
    line-height: 16px;
    border-radius: var(--spacing-0-5);
  }

  &.-small {
    height: 32px;
    padding: 8px;
    font-size: 14px;
    line-height: 16px;
  }

  &.-medium {
    height: 40px;
    padding: 12px;
    font-size: 14px;
    line-height: 16px;
  }

  &.-large {
    height: 52px;
    padding: 16px;
    font-size: 16px;
    line-height: 20px;
  }

  &.-primary {
    @apply tw-bg-primary-500;
    color: white;

    &::v-deep .nds-icon {
      @apply !tw-text-white;
      @apply !tw-stroke-white;
    }

    &:hover {
      @apply tw-bg-primary-600;
    }

    &:active {
      @apply tw-bg-primary-700;
    }
  }

  &.-secondary {
    background-color: white;
    border-width: 1px;
    border-style: solid;
    @apply tw-border-neutral-200;
    @apply tw-text-neutral-950;

    &::v-deep .nds-icon {
      @apply !tw-text-neutral-950;
      @apply !tw-stroke-neutral-950;
    }

    &:hover {
      @apply tw-bg-neutral-50;
    }

    &:active {
      @apply tw-bg-neutral-100;
    }
  }

  &.-tertiary {
    background-color: transparent;
    border: 1px solid transparent;
    @apply tw-text-primary-500;

    &::v-deep .nds-icon {
      color: var(--primary-500) !important;
      stroke: var(--primary-500) !important;
    }

    &:hover {
      @apply tw-bg-alpha-700;
    }

    &:active {
      @apply tw-bg-neutral-100;
    }

    &:disabled,
    &.-disabled {
      background-color: transparent;
      color: var(--neutral-700);
    }
  }

  &.-destructive {
    @apply tw-bg-danger-50;
    border-width: 1px;
    border-style: solid;
    @apply tw-border-danger-50;
    @apply tw-text-danger-500;

    &::v-deep .nds-icon {
      @apply !tw-text-danger-500;
      @apply !tw-stroke-danger-500;
    }

    &:hover {
      @apply tw-bg-danger-100;
    }

    &:active {
      @apply tw-bg-danger-200;
    }
  }

  /* To be merged with tertiary when design is done */

  &.-transparent {
    @apply tw-text-neutral-900;
    background-color: transparent;
    border-color: transparent;

    &:hover {
      background-color: rgba(35, 36, 36, 0.07);
    }
  }

  &:disabled,
  &.-disabled {
    cursor: not-allowed;
    pointer-events: none;
    color: var(--neutral-700);
    background-color: var(--neutral-200);

    &::v-deep .nds-icon {
      color: var(--neutral-700) !important;
      stroke: var(--neutral-700) !important;
    }

    &:hover {
      color: var(--neutral-700);
      background-color: var(--neutral-200);
    }

    &.ghost {
      background-color: transparent;
    }
  }
}
</style>
